import { useState } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { ExpenseUseCaseErrors } from '@webapp/modules/expense/business-logic/use-cases/ExpenseUseCaseErrors'
import messages from './messages'
import { useMutateDuplicateExpenseReport } from '../../models/useMutateDuplicateExpenseReport'

interface UseDuplicateExpenseReportPresenterParams {
  expenseId: number
  onSuccess: () => void
}

export const useDuplicateExpenseReportPresenter = ({
  expenseId,
  onSuccess,
}: UseDuplicateExpenseReportPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutate, isLoading } = useMutateDuplicateExpenseReport()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const openConfirm = () => {
    setIsConfirmOpen(true)
  }

  const closeConfirm = () => {
    setIsConfirmOpen(false)
  }

  const duplicate = async (confirmOverwriteDraft: boolean = false) => {
    try {
      await mutate({ id: expenseId, confirmOverwriteDraft })

      toast.success({
        title: intl.formatMessage(messages.duplicateSuccess, { expenseId }),
      })

      onSuccess()
    } catch (error) {
      if (
        error instanceof ExpenseUseCaseErrors &&
        error.name === 'DUPLICATE_REQUIRE_CONFIRMATION_TO_OVERWRITE_DRAFT'
      ) {
        setIsConfirmOpen(true)
        return
      }

      toast.error({
        title: getMessageFromError(error),
      })
    }
  }

  const handleConfirm = () => {
    setIsConfirmOpen(false)
    duplicate(true)
  }

  return {
    commands: {
      duplicate: () => duplicate(false),
      openConfirm,
      closeConfirm,
      confirm: handleConfirm,
    },
    states: {
      isDuplicating: isLoading,
      isConfirmOpen,
    },
  }
}
